/**
 * PATH: packages/email-hub/src/_internal/components/AnchoredPopover.tsx
 * FILE_ID: EMAILHUB-INTERNAL-COMPONENT-ANCHOREDPOPOVER-001
 * PURPOSE: Body-portalled popover positioned against an anchor element.
 *          Replaces the `absolute top-full` in-flow dropdown pattern for
 *          pickers that live inside overflow-clipped containers.
 * WHY:     BUG-2026-07-17-TAG-PICKER-CLIPPED — the reader Signal Strip
 *          (2026-07-16 redesign) wraps its lanes in `overflow-hidden` +
 *          `overflow-x-auto`, so the tag/category pickers (plain
 *          `absolute z-50 top-full` divs) were clipped to a ~40px sliver
 *          and could not be used at all from the split-view reader —
 *          operator: "can't tag, can't do anything properly here".
 *          Rendering through a document.body portal with fixed
 *          positioning makes the picker immune to ANY ancestor clipping.
 * FLOW:    open → measure anchor rect → fixed-position div portalled to
 *          body (flips above the anchor when the space below is tight)
 *          → repositions on scroll/resize → unmounts on close.
 * DEPENDENCIES: react-dom createPortal only.
 * RELATED:
 *   - feature/components/category-filter-bar/useAnchoredMenu.ts: sibling
 *     RIGHT-anchored positioning hook for the category-bar More/View
 *     menus (same portal-out-of-clip idea, different anchoring edge and
 *     no flip). If a third variant is ever needed, converge these two
 *     instead of forking again.
 *   - feature/components/EmailTagChips.tsx: tag picker consumer.
 *   - feature/components/CategoryChip.tsx: category picker consumer.
 *   - feature/components/signal-strip/SignalStrip.tsx: the clipping
 *     container that motivated this.
 */

import {
  useLayoutEffect,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

// Above the compose dock (40/60) and the reader panes; below nothing
// that should ever cover an open picker.
const POPOVER_Z = 80
const VIEWPORT_MARGIN = 8
// Flip above the anchor when less than this remains below it AND the
// space above is larger — keeps short pickers below by default.
const FLIP_THRESHOLD_PX = 240

interface AnchoredPopoverProps {
  /** The element the popover attaches to (the chip/trigger's wrapper). */
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  /** Styling for the popover surface (border/background/padding). Do
   *  not pass positioning classes — position is computed inline. */
  className?: string
  children: ReactNode
  /** Populated with the live popover element so the OWNER's click-away
   *  handler can treat clicks inside the portal as "inside". */
  popoverRef?: MutableRefObject<HTMLDivElement | null>
  role?: string
}

export function AnchoredPopover({
  anchorRef,
  open,
  className,
  children,
  popoverRef,
  role,
}: AnchoredPopoverProps) {
  const [style, setStyle] = useState<CSSProperties | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null)
      return
    }
    const el = anchorRef.current
    if (!el) return
    const update = () => {
      const r = el.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      // Clamp so a chip near the right edge doesn't push the popover
      // off-canvas (min-width of the pickers is ~180px).
      const left = Math.min(
        Math.max(VIEWPORT_MARGIN, r.left),
        Math.max(VIEWPORT_MARGIN, vw - 200),
      )
      const spaceBelow = vh - r.bottom
      const openUp = spaceBelow < FLIP_THRESHOLD_PX && r.top > spaceBelow
      setStyle(
        openUp
          ? {
              position: 'fixed',
              left,
              bottom: vh - r.top + 4,
              zIndex: POPOVER_Z,
              maxHeight: Math.max(
                120,
                Math.min(r.top - VIEWPORT_MARGIN, vh * 0.6),
              ),
            }
          : {
              position: 'fixed',
              left,
              top: r.bottom + 4,
              zIndex: POPOVER_Z,
              maxHeight: Math.max(
                120,
                Math.min(spaceBelow - VIEWPORT_MARGIN, vh * 0.6),
              ),
            },
      )
    }
    update()
    window.addEventListener('resize', update)
    // Capture-phase scroll so repositioning tracks ANY scrolling
    // ancestor (reader pane, signal-strip lane, page).
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef])

  if (!open || !style || typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={(node) => {
        if (popoverRef) popoverRef.current = node
      }}
      style={style}
      className={className}
      role={role}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  )
}
